import { useCallback, useEffect, useState } from "react";
import { Bell } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { BRAND } from "@/lib/constants";
import api, { getErrorMessage } from "@/lib/api";

/**
 * NotificationsTab — Settings → Notifications.
 *
 * v1 surface: a single toggle controlling auto-pause-on-2FA-failure.
 *
 * Data flow:
 *   - On mount, GET /api/client/me, read
 *     `notification_preferences.scraper_pause_2fa.enabled` (defaults to true
 *     when the field is missing).
 *   - On toggle ON, persist immediately via PUT /api/client/settings.
 *   - On toggle OFF, open an AlertDialog explaining the consequences
 *     (account-lockout risk); persist only when the user confirms.
 *
 * The toggle controls BOTH the auto-pause behavior and the email — per the
 * locked design decision in vault/90-Active/PLAN-b-tenant-2fa-pause-email.md.
 * When this is off the cron's auto-pause orchestrator still increments the
 * counter, but the pause flag does NOT flip and no email is sent.
 */
export default function NotificationsTab() {
  const [enabled, setEnabled] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/client/me");
        if (cancelled) return;
        const prefs = res.data?.notification_preferences?.scraper_pause_2fa;
        if (prefs && typeof prefs.enabled === "boolean") {
          setEnabled(prefs.enabled);
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(
            getErrorMessage(err, "Couldn't load notification settings")
          );
        }
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const persistChange = useCallback(
    async (newEnabled) => {
      const prevEnabled = enabled;
      setSaving(true);
      setEnabled(newEnabled);
      try {
        await api.put("/client/settings", {
          notification_preferences: {
            scraper_pause_2fa: { enabled: newEnabled },
          },
        });
        toast.success(
          newEnabled
            ? "Auto-pause notifications enabled"
            : "Auto-pause notifications disabled"
        );
      } catch (err) {
        setEnabled(prevEnabled);
        toast.error(
          getErrorMessage(err, "Couldn't save notification settings")
        );
      } finally {
        setSaving(false);
      }
    },
    [enabled]
  );

  const handleSwitchChange = (next) => {
    if (!next && enabled) {
      setConfirmOpen(true);
    } else {
      persistChange(next);
    }
  };

  const handleConfirmOff = () => {
    setConfirmOpen(false);
    persistChange(false);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" style={{ color: BRAND.text }} />
            Auto-pause on 2FA failures
          </CardTitle>
          <CardDescription>
            If we can&apos;t receive a 2FA code for 24 hours, we&apos;ll pause
            that connection and email you. This protects your portal accounts
            from getting locked out by repeated failed logins.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-700">
                Notify me about 2FA-pause events
              </div>
              <Badge
                variant="outline"
                className="text-green-700 border-green-200 bg-green-50"
              >
                Recommended: On
              </Badge>
            </div>
            <Switch
              id="scraper-pause-2fa-toggle"
              aria-label="Auto-pause notifications"
              checked={enabled}
              disabled={!loaded || saving}
              onCheckedChange={handleSwitchChange}
            />
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Turn off auto-pause?</AlertDialogTitle>
            <AlertDialogDescription>
              When this is off, your scrapers will keep retrying every 12
              hours even when 2FA codes aren&apos;t arriving. Repeated failed
              logins can cause your portal accounts to lock out — we
              recommend leaving auto-pause on.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmOff}>
              Turn off anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
